import React, { CSSProperties, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import TableHeader from './Header'
import Pagination from './Pagination'
import Search from './Search'
import { LinkContainer } from './style'
import { useUpdateDataTable } from '../../hooks/dataTable'
import { useLoading } from '../../hooks/loading'
import { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface Action {
  name: string
  icon?: IconDefinition
  spanIcon?: string
  htmlIcon?: JSX.Element
  title: string
  link?: string
  classname?: string
  activeClassname?: string
  style?: CSSProperties
  onClick?: (params: any) => void
  hasIcon?: (params: any) => boolean
  linkTo?: (params: any) => string
}

export interface Header {
  name: string
  field: string
  sortable: boolean
  download?: {
    fileName?: string
    fileUrl?: string
  }
  hasTypes?: {
    types?: any
  }
  excludesFields?: [
    {
      field: string
      value: string
    }
  ]
  includesFields?: [
    {
      field: string
      value: string
      style?: CSSProperties
    }
  ]
  concat?: {
    field: string
    position: 'start' | 'end'
  }
}

interface SearchParameters {
  [key: string]: string
}

interface DataTableProps {
  onActions?: {
    onClickButtonEdit?: <T>(currentValue: T | any) => void
    onClickButtonRemove?: <T>(currentValue: T | any) => void
    onClickButtonList?: <T>(currentValue: T | any) => void
  }
  entity: string
  source: string
  headers?: Header[]
  actions?: Action[]
  notHasChildren?: boolean
  onlyParent?: boolean
  notParent?: boolean
  onlyView?: boolean
  parentId?: string
  viewField?: { source: string; field: string }
  entityId?: string
  searchParameters?: SearchParameters[]
  showParentId?: boolean
  totalColumn?: string
  format: {
    orderBy: string
  }
  excludeColumn?: string
  excludeKey?: string
  activeItems?: number[]
}

const DataTable = ({
  onActions,
  entity,
  source,
  notHasChildren,
  viewField,
  onlyView,
  onlyParent,
  notParent,
  excludeColumn,
  excludeKey,
  headers = [
    { name: 'Data', field: 'created_at', sortable: true },
    { name: 'Descrição', field: 'descriptions', sortable: true },
    { name: 'Ação', field: 'type', sortable: true }
  ],
  actions,
  format,
  parentId,
  entityId,
  searchParameters,
  showParentId,
  activeItems,
  totalColumn
}: DataTableProps): JSX.Element => {
  const [items, setItems] = useState<any[]>([])
  const [filterItems, setFilterItems] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [ItemsPerPage, setItemsPerPage] = useState(50)
  const { isUpdated } = useUpdateDataTable()

  const { activeLoading, disableLoading } = useLoading()

  const handlerOnClickButtonList = (currentValue: any) => {
    if (viewField) {
      return `/${viewField.source}/view/${currentValue[viewField.field]}?tab=${
        currentValue.id
      }`
    }

    if (typeof onActions?.onClickButtonList === 'function') {
      onActions.onClickButtonList(currentValue)
    } else {
      return `/${source}/view/${currentValue.id}`
    }
  }

  const handlerOnClickButtonEdit = (currentValue: any) => {
    return `/${source}/update/${currentValue.id}`
  }

  const handlerOnClickButtonRemove = (currentValue: any) => {
    if (onActions?.onClickButtonRemove) {
      onActions.onClickButtonRemove(currentValue)
    }
  }

  const loadParams = useCallback(() => {
    const params = {
      entity,
      source,
      keyword: '',
      page: currentPage,
      perPage: ItemsPerPage,
      orderByField: '',
      searchParameters,
      onlyParent,
      notParent,
      orderBy: format.orderBy,
      parentId,
      entityId,
      excludeColumn,
      excludeKey
    }

    if (!parentId) Object.assign(params, { parentId: '' })
    if (!entityId) Object.assign(params, { entityId: '' })

    return params
  }, [
    ItemsPerPage,
    currentPage,
    entity,
    entityId,
    excludeColumn,
    excludeKey,
    format.orderBy,
    onlyParent,
    notParent,
    parentId,
    searchParameters,
    source
  ])

  useEffect(() => {
    ;(async () => {
      try {
        activeLoading()
        const params = loadParams()
        const response = await api.get('dataTable', { params })

        setItems(response.data.items)
        setFilterItems(response.data.items)
        setTotalItems(response.data.totalItens)
        setCurrentPage(response.data.page)
        disableLoading()
      } catch (error) {
        disableLoading()
        console.error(error)
      }
    })()
  }, [loadParams, isUpdated, activeLoading, disableLoading])

  const firstItem =
    totalItems === 0 ? totalItems : ItemsPerPage * (currentPage - 1) + 1

  const getTotalItems = (initialValue: number): number => {
    let sum = 0
    if (initialValue > 1) {
      return items.length + initialValue - 1
    } else {
      if (notHasChildren) {
        sum = items.reduce((sum, value) => {
          if (!showParentId && !value.parent_id) {
            return sum + 1
          }
          if (showParentId) {
            return sum + 1
          }
          return sum
        }, 0)
      } else {
        sum = items.length
      }
    }
    return sum
  }

  const getSearched = async (value: string) => {
    const params = loadParams()
    const response = await api.get('dataTable', {
      params: {
        ...params,
        keyword: { [params.orderBy]: value }
      }
    })
    setItems(response.data.items)
  }

  let timeOutId: NodeJS.Timeout
  const onSearchItem = async (value: string) => {
    if (value.length === 0) {
      setItems(filterItems)
      clearTimeout(timeOutId)
      return
    }

    clearTimeout(timeOutId)
    timeOutId = setTimeout(() => getSearched(value), 500)
  }

  const handleDownload = useCallback((item: any, header: Header) => {
    let filename = item[header.download.fileName]
    const indexTrace = filename.indexOf('-') + 1
    filename = filename.slice(indexTrace, filename.length)
    fetch(`${item[header.download.fileUrl]}`).then(response => {
      response.blob().then((blobObject: any) => {
        const url = window.URL.createObjectURL(blobObject)
        const a = document.createElement('a')
        a.download = filename
        a.href = url
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      })
    })
  }, [])

  return (
    <div id="kt_datatable">
      <div className="card-header p-0 align-items-center py-5 gap-2 gap-md-5">
        <div className="">
          <div id="kt_datatable" className="dataTables_length">
            <label>
              <select
                onChange={e => setItemsPerPage(Number(e.target.value))}
                className="form-select form-select-solid form-select-lg fw-boldl"
              >
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>{' '}
              <p className="">resultados por página</p>
            </label>
          </div>
        </div>
        <div className="">
          <div className="dataTables_filter">
            <label className="fw-bolder fs-6 text-gray-800">
              Pesquisar
              <Search onSearch={value => onSearchItem(value)} />
            </label>
          </div>
        </div>
      </div>
      <div className="table-scrollable dataTable">
        <table
          id="kt_datatable"
          className="dataTable table table-striped table-row-bordered gs-3 border table-hover"
        >
          <TableHeader
            headers={headers}
            onSorting={(field, order) => {
              const itemSorted = items.sort((a, b) => {
                if (
                  ((totalColumn && a[totalColumn]) || b[totalColumn])
                    .toLowerCase()
                    .includes('total')
                ) {
                  return 0
                }
                const fields = field.split('.')
                let currentFieldA
                let currentFieldB
                if (fields.length === 1) {
                  currentFieldA = a[fields[0]]
                  currentFieldB = b[fields[0]]
                }
                if (fields.length === 2) {
                  currentFieldA = a[fields[0]]?.[fields[1]]
                  currentFieldB = b[fields[0]]?.[fields[1]]
                }
                if (fields.length === 3) {
                  currentFieldA = a[fields[0]]?.[fields[1]]?.[fields[2]]
                  currentFieldB = b[fields[0]]?.[fields[1]]?.[fields[2]]
                }
                if (fields.length === 4) {
                  currentFieldA =
                    a[fields[0]]?.[fields[1]]?.[fields[2]]?.[fields[3]]
                  currentFieldB =
                    b[fields[0]]?.[fields[1]]?.[fields[2]]?.[fields[3]]
                }
                return currentFieldA > currentFieldB && order === 'ASC' ? 1 : -1
              })
              setItems([...itemSorted])
            }}
          />
          <tbody>
            {(items.length > 0 &&
              items.map(item => {
                const types = [
                  { key: 'locacao', label: 'LOCAÇÃO' },
                  { key: 'L', label: 'LOCAÇÃO' },
                  { key: 'materia-prima', label: 'MATERIA PRIMA' },
                  { key: 'revenda', label: 'REVENDA' },
                  { key: 'semi-acabado', label: 'SEMI ACABADO' },
                  { key: 'consumo', label: 'USO E CONSUMO' },
                  { key: 'CONSUMO', label: 'USO E CONSUMO' },
                  { key: 'venda', label: 'VENDA' },
                  { key: 'V', label: 'VENDA' },
                  { key: 'C', label: 'Crédito' },
                  { key: 'D', label: 'Débito' }
                ]
                const typeProduct = types.find(
                  t => t.key === item.product?.type
                )
                if (typeProduct) {
                  item.product.type = typeProduct.label
                }
                const type = types.find(t => t.key === item.type)
                if (type) {
                  item.type = type.label
                }
                return (
                  <tr key={item.id}>
                    {headers.map(
                      header =>
                        (header?.field !== 'actions' &&
                          !header?.download &&
                          !header?.hasTypes && (
                            <td
                              key={`${header?.field}-${item.id}`}
                              className="border"
                            >
                              <p
                                style={{
                                  textAlign: 'left',
                                  ...header?.includesFields?.find(
                                    includeItem =>
                                      includeItem?.value ===
                                      item[includeItem?.field]
                                  )?.style
                                }}
                              >
                                {typeof item[header?.field] === 'boolean' &&
                                  (item[header?.field] ? 'Sim' : 'Não')}
                                {header?.field.split('.').length === 4 &&
                                  item[header?.field.split('.')[0]]?.[
                                    header?.field.split('.')[1]
                                  ]?.[header?.field.split('.')[2]]?.[
                                    header?.field.split('.')[3]
                                  ]}
                                {header?.field.split('.').length === 3 &&
                                  item[header?.field.split('.')[0]]?.[
                                    header?.field.split('.')[1]
                                  ]?.[header?.field.split('.')[2]]}
                                {header?.field.split('.').length === 2 &&
                                  item[header?.field.split('.')[0]]?.[
                                    header?.field.split('.')[1]
                                  ]}
                                {header?.concat
                                  ? header?.concat?.position === 'start'
                                    ? `${item[header?.concat?.field]} ${
                                        item[header?.field]
                                      }`
                                    : `${item[header?.field]} ${
                                        item[header?.concat?.field]
                                      }`
                                  : header?.field.split('.').length === 1 &&
                                    item[header?.field]}
                              </p>
                            </td>
                          )) ||
                        (header?.download && (
                          <td
                            key={`${header?.field}-${item.id}`}
                            className="border"
                          >
                            <a
                              href="#"
                              onClick={() => handleDownload(item, header)}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start'
                              }}
                            >
                              <span
                                className="fa fa-download"
                                style={{ marginRight: '5px' }}
                              ></span>
                              <span>Download</span>
                            </a>
                          </td>
                        )) ||
                        (header?.hasTypes && (
                          <td
                            key={`${header?.field}-${item.id}`}
                            className="border"
                          >
                            <p style={{ textAlign: 'left' }}>
                              {header.hasTypes.types[item[header?.field]]}
                            </p>
                          </td>
                        )) || (
                          <td
                            key={`actions-${item.id}`}
                            className="actions border"
                          >
                            {!header.excludesFields?.some(
                              excludeItem =>
                                excludeItem.value === item[excludeItem.field]
                            ) ? (
                              (actions && (
                                <LinkContainer>
                                  {actions.map(
                                    action =>
                                      (action.hasIcon
                                        ? action.hasIcon(item)
                                        : true) && (
                                        <Link
                                          className={
                                            activeItems?.length &&
                                            activeItems.includes(item.id)
                                              ? `${action.classname} ${action.activeClassname}`
                                              : `${action.classname}`
                                          }
                                          to={
                                            action.link ||
                                            (action.linkTo &&
                                              action.linkTo(item)) ||
                                            '#'
                                          }
                                          key={action.name}
                                          title={action.title}
                                          onClick={e => {
                                            if (action.onClick) {
                                              e.preventDefault()
                                              action.onClick(item)
                                            }
                                          }}
                                        >
                                          {action.spanIcon && (
                                            <span
                                              className={action.spanIcon}
                                              style={action.style}
                                            />
                                          )}
                                          {action.icon && (
                                            <FontAwesomeIcon
                                              icon={action.icon}
                                            />
                                          )}
                                          {action.htmlIcon && action.htmlIcon}
                                        </Link>
                                      )
                                  )}
                                </LinkContainer>
                              )) || (
                                <LinkContainer>
                                  {showParentId && (
                                    <Link
                                      className="link"
                                      key={Math.random()}
                                      title="Visualizar"
                                      to={() => handlerOnClickButtonList(item)}
                                    >
                                      <span className="fa fa-search" />
                                    </Link>
                                  )}
                                  {!showParentId && !item.parent_id && (
                                    <Link
                                      className="link"
                                      key={Math.random()}
                                      title="Visualizar"
                                      to={() => handlerOnClickButtonList(item)}
                                    >
                                      <span className="fa fa-search" />
                                    </Link>
                                  )}
                                  <div>
                                    {!onlyView && (
                                      <Link
                                        className="link"
                                        key={Math.random()}
                                        title="Editar"
                                        onClick={e => {
                                          if (onActions?.onClickButtonEdit) {
                                            e.preventDefault()
                                            onActions.onClickButtonEdit(item)
                                          }
                                        }}
                                        to={() =>
                                          handlerOnClickButtonEdit(item)
                                        }
                                      >
                                        <span className="fa fa-edit" />
                                      </Link>
                                    )}
                                    {!notHasChildren && (
                                      <a
                                        href="#"
                                        key={Math.random()}
                                        title="Remover"
                                        className="link"
                                        onClick={() => {
                                          handlerOnClickButtonRemove(item)
                                        }}
                                      >
                                        <span className="fa fa-remove" />
                                      </a>
                                    )}
                                  </div>
                                </LinkContainer>
                              )
                            ) : (
                              <></>
                            )}
                          </td>
                        )
                    )}
                  </tr>
                )
              })) || (
              <tr>
                <td colSpan={headers.length} style={{ textAlign: 'center' }}>
                  Nenhum registro encontrado
                </td>
              </tr>
            )}
          </tbody>
          <tfoot />
        </table>
      </div>
      <div className="d-flex flex-stack align-items-end h-60px">
        <div className="">
          <div className="dataTables_info" id="kt_datatable">
            Mostrando de {firstItem} até {getTotalItems(firstItem)} de{' '}
            {totalItems} registros
          </div>
        </div>
        <div className="">
          <div className="dataTables_paginate paging_bootstrap_number">
            <Pagination
              total={totalItems}
              itemsPerPage={ItemsPerPage}
              currentPage={currentPage}
              onPageChange={page => setCurrentPage(page)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default DataTable
